import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getAreaList, getMarketGetAllMainRegions } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { RHFTextField } from "@/components/RHF/RHFTextField";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { FieldName } from "@/pages/SV/MarketCreate/MarketCreate.constants";
import { setMarketCreatePageOptionsCache } from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";

interface LocationSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const LocationSection = ({
    activeMap,
    resetBelow,
}: LocationSectionProps) => {
    const dispatch = useAppDispatch();
    const { optionCache } = useAppSelector((s) => s.ui.marketCreatePage);

    const { getValues } = useFormContext<NewMarketFormValues>();
    const region = getValues("region");

    const [isAllRegions, setIsAllRegions] = useState(false);

    const handleAllRegions = useCallback(() => {
        setIsAllRegions(true);
    }, []);

    const { options: fetchedOptions, loading: loadingRegions } =
        useAsyncOptions({
            fetchFn: (signal) => {
                if (isAllRegions) {
                    return getAreaList();
                }

                const partnerId = getValues("partner");

                return getMarketGetAllMainRegions(
                    {
                        partnerId,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                );
            },
            selectData: (response) => Object.values(response.result),
            mapToOption: (c) => ({ label: c.name, value: c.id }),
            dependencies: [isAllRegions],
            enabled: activeMap.region && (!region || isAllRegions),
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "region",
                    options: fetchedOptions,
                }),
            );
        }
    }, [dispatch, fetchedOptions]);

    return (
        <>
            <RHFAutocomplete
                name="region"
                options={optionCache.region ?? []}
                label="Регіон"
                disabled={!activeMap.region}
                onAfterChange={() => resetBelow("region")}
                allowClear={false}
                rules={{
                    required: validationMessage.required,
                }}
                loading={loadingRegions}
                actionOption={
                    !isAllRegions
                        ? {
                              label: "Всі регіони",
                              onClick: handleAllRegions,
                          }
                        : undefined
                }
                selectProps={{ size: "large" }}
            />

            <RHFTextField
                name="name"
                label="Назва (опціонально)"
                disabled={!activeMap.name}
                onAfterChange={() => resetBelow("name")}
                size="large"
            />
        </>
    );
};
